import type { FC, ReactNode } from 'react'
import type { Author } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Avatar } from '@/components/blog/avatar'

interface AuthorCardProps {
    author: Author
    headingLevel?: 'h2' | 'h3'
    className?: string
}

export const AuthorCard: FC<AuthorCardProps> = ({ author, headingLevel = 'h3', className }): ReactNode => {
    const Heading = headingLevel

    return (
        <article className={cn('group relative flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors hover:border-foreground/25', className)}>
            <Avatar src={author.avatar} alt={author.avatarAlt} name={author.name} className='size-20 text-xl' />

            <Heading className='mt-6 font-heading text-2xl font-medium tracking-tight'>
                <Link
                    href={`/authors/${author.slug}`}
                    className='after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:outline-2 focus-visible:after:outline-offset-4 focus-visible:after:outline-ring'
                >
                    {author.name}
                </Link>
            </Heading>

            {author.role && (
                <p className='mt-1 text-sm font-medium text-brand'>
                    {author.role}
                </p>
            )}

            {author.bio && (
                <p className='mt-4 text-[0.95rem] leading-relaxed text-muted-foreground line-clamp-4'>
                    {author.bio}
                </p>
            )}

            <p className='mt-auto flex items-center justify-between pt-6 text-sm font-medium text-foreground'>
                <span>
                    {author.postCount === 1 ? '1 story' : `${author.postCount} stories`}
                </span>

                <ArrowRight className='size-4 transition-transform duration-300 group-hover:translate-x-1' aria-hidden='true' />
            </p>
        </article>
    )
}
